import { getScopedTranslate } from '@locale/translate';

import type {
  FieldMappingEditorValue,
  FieldMappingFieldItem,
  FieldMappingGroup,
} from './field-mapping';

const locale = {
  en: {
    traveller: 'Traveller',
    company: 'Company',
    journeyDetails: 'Journey details',
    journeyData: 'Journey data',
    action: 'Action',
  },
};

export const labelSeparator = ' • ';

export const JOURNEY_ATTRIBUTE_PREFIX = 'journey_attribute__';
export const COMPANY_TRAVELLER_ATTRIBUTE_PREFIX = 'company_traveller_attribute__';
const ATTRIBUTE_SOURCE_PREFIXES = [JOURNEY_ATTRIBUTE_PREFIX, COMPANY_TRAVELLER_ATTRIBUTE_PREFIX];

export const isAttributeSource = (source = '') =>
  ATTRIBUTE_SOURCE_PREFIXES.some((prefix) => source.startsWith(prefix));

export const isTravellerVariableSource = (variableSource: FieldMappingFieldItem['source'] = '') => {
  return (
    variableSource === 'company_traveller' ||
    variableSource === 'traveller' ||
    variableSource === 'traveller_journey' ||
    isAttributeSource(variableSource)
  );
};

// A variable whose source/action was deleted or severed carries a `_MISSING`
// suffix (mirrors the backend MISSING_SOURCE_ACTION_POSTFIX).
export const MISSING_SOURCE_SUFFIX = '_MISSING';

export const FORM_LINK_FIELD = 'unique_link';

const isVariableOfferedByGroups = (
  { source, action }: { source: string; action: string },
  variableGroups: FieldMappingGroup[],
): boolean => {
  if (source === 'action') {
    return variableGroups.some((group) => group.id === action);
  }
  if (isAttributeSource(source)) {
    return variableGroups.some((group) => group.fields.some((entry) => entry.source === source));
  }
  return true;
};

// The single frontend definition of "broken" -- editor pill, header chips and
// preview helpers all call this; don't add a second. With groups it also flags
// a variable whose journey-specific id no group offers: an attribute source
// `<prefix>__<id>` or an action short id. Static sources (traveller, company,
// journey, traveller_journey, journey_data) carry no such id and are never
// flagged. A group marked `isPending` means its builder's queries are still in
// flight or failed without data, so the catalogue is unknown; absent groups
// mean the editor has no catalogue at all.
export const isVariableSourceMissing = (
  { source = '', action = '' }: { source?: string; action?: string },
  variableGroups?: FieldMappingGroup[],
): boolean => {
  const hasMissingMarker =
    (source === 'action' && action.endsWith(MISSING_SOURCE_SUFFIX)) ||
    (isAttributeSource(source) && source.endsWith(MISSING_SOURCE_SUFFIX));
  if (hasMissingMarker) return true;
  if (!variableGroups || variableGroups.some((group) => group.isPending)) return false;
  return !isVariableOfferedByGroups({ source, action }, variableGroups);
};

export const hasUnavailableVariables = (
  value: unknown,
  variableGroups: FieldMappingGroup[] | undefined,
): boolean => {
  if (Array.isArray(value)) {
    return value.some((item) => hasUnavailableVariables(item, variableGroups));
  }
  if (!value || typeof value !== 'object' || !('variables' in value)) return false;
  const variables = (value as FieldMappingEditorValue).variables;
  if (!variables) return false;
  return Object.values(variables).some((variable) =>
    isVariableSourceMissing(variable, variableGroups),
  );
};

const getVariableSource = (
  variable: FieldMappingFieldItem,
  variableGroups?: FieldMappingGroup[],
): string => {
  const { t } = getScopedTranslate(locale);

  // The group a variable came from carries the product's own localised name for
  // that source, so it wins over anything named here: Hire says 'Þjóðskrá' and
  // 'Fyrirtæki' to an Icelandic user, where this module's locale block is
  // English-only. Group ids are the source name — or the action id for an
  // action-sourced variable, which is why that branch was already a lookup.
  //
  // For Journeys this changes nothing: its group labels are the same strings
  // named below ('Traveller', 'Journey details', 'Company').
  const groupId = variable.source === 'action' ? variable.action : variable.source;
  const group = groupId ? variableGroups?.find((entry) => entry.id === groupId) : undefined;
  if (group?.label) {
    return group.label;
  }

  if (isTravellerVariableSource(variable.source)) {
    return t('traveller');
  }
  if (variable.source === 'company') {
    return t('company');
  }
  if (variable.source === 'journey') {
    return t('journeyDetails');
  }
  if (variable.source === 'journey_data') {
    return t('journeyData');
  }
  if (variable.source === 'action') {
    // The group lookup above already tried the action's own label; reaching
    // here means its group is gone (a severed action).
    return t('action');
  }
  // Everything named above resolves to a capitalised name, and so does a group
  // label. Anything left is the raw source string, which is lowercase — this is
  // the groupless preview path for Hire's `application` / `job` / `form` /
  // `ssnregistry`. Capitalise so it never reads as "application • …" beside a
  // "Company • …".
  //
  // `?? ''` rather than a bare return: a variable with no source used to render
  // the literal string "undefined • Field".
  return capitalizeFirst(variable.source ?? '');
};

const capitalizeFirst = (text: string): string =>
  text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;

type VariableFieldRef = Partial<Pick<FieldMappingFieldItem, 'source' | 'field' | 'action'>>;

// Action variables match on action, others on source. `field` alone is not
// unique (all simple attributes use `value`), and an action variable carrying
// no action id must resolve to nothing rather than to another action's
// identically-keyed field.
export const findVariableField = (
  variable: VariableFieldRef,
  fields: FieldMappingFieldItem[],
): FieldMappingFieldItem | undefined => {
  const actionId = variable.action && variable.action !== 'undefined' ? variable.action : undefined;
  const isActionVariable = variable.source === 'action' || actionId !== undefined;
  return fields.find((field) => {
    if (field.field !== variable.field) {
      return false;
    }
    // `field.action` is undefined on every non-action field, so an action
    // variable with no usable id must fail rather than compare undefined.
    return isActionVariable
      ? actionId !== undefined && field.action === actionId
      : field.source === variable.source;
  });
};

// Traveller and attribute sources identify their group by its fields rather
// than by a group id, so that arm delegates to findVariableField.
export const findVariableGroup = (
  variable: VariableFieldRef,
  variableGroups: FieldMappingGroup[],
): FieldMappingGroup | undefined => {
  return variableGroups.find((group) => {
    if (variable.action && group.id === variable.action) {
      return true;
    }
    if (isTravellerVariableSource(variable.source)) {
      return Boolean(findVariableField(variable, group.fields));
    }
    if (variable.source === 'company' && group.id === 'company') {
      return true;
    }
    if (variable.source === 'journey_data' && group.id === 'journeyData') {
      return true;
    }
    return group.id === variable.source;
  });
};

export const getVariableLabel = (
  variable: FieldMappingFieldItem,
  variableGroups?: FieldMappingGroup[],
) => {
  // Gate on the broken marker, not a failed group lookup: the previews call this
  // without variableGroups (every lookup fails there, but healthy vars still need
  // a prefix). A broken var's label is already a qualified "Source • Field", so
  // return as-is -- prepending "Action" would give "Action • Email • Subject".
  if (isVariableSourceMissing(variable)) {
    return variable.label;
  }

  const prettySource = getVariableSource(variable, variableGroups);
  const variableLabel =
    variableGroups
      ?.map((group) => findVariableField(variable, group.fields)?.label)
      .find((label) => label !== undefined) ?? variable.label;

  const prefix = `${prettySource}${labelSeparator}`;
  // Case-insensitive: pillToVariable stores the rendered label back onto the
  // variable, so a value saved before sources were capitalised carries a
  // lowercase prefix. The preview path has no groups to re-derive a plain label
  // from, and a case-sensitive test would answer "Application • application •
  // Field". Returning the stored label as-is leaves it looking dated until
  // something re-renders it with groups, which beats duplicating the prefix.
  if (variableLabel.toLowerCase().startsWith(prefix.toLowerCase())) {
    return variableLabel;
  }
  return `${prefix}${variableLabel}`;
};
